import {
    ViewChild,
    Component,
    AfterViewInit,
    ViewContainerRef,
} from "@angular/core";
import { distinctUntilChanged, map, Observable, withLatestFrom } from "rxjs";

import { PdfRendererComponent } from "../../print/pdf/pdf-renderer.component";
import { SignModal } from "../../components/modals/sign-modal.component";
import { SignAllModal } from "../../components/modals/sign-all-modal.component";
import { QRCodeModal } from "../../components/modals/qr-code-modal.component";
import { EmailFormModal } from "../../components/modals/email-form-modal.component";
import { SignpadService } from "../esign/signpad.service";
import { EsignService, EsignViewInfo } from "../esign/esign.service";
import {
    ConsultationProcess,
    ConsultationService,
    Division,
} from "../../services/consultation.service";
import { Store } from "@ngrx/store";
import { RootState } from "../../store/root";
import { stateBuilder } from "../../store/utils";
import { PersistenceService } from "../../store/model/persistence.service";
import { ValidityState } from "../../store/validity/state";
import { FinalizingModal } from "../../components/modals/finalizing-modal.component";
import { DocumentState, Signature } from "../../store/document/state";
import * as DocActions from "../../store/document/actions";
import { UtilsComponent } from "../../components/utils.component";
import { KeycloakService } from "../../security";
import { UserInfo } from "../../data/product";
import { Helpers } from "../../utils/browser";

@Component({
    selector: "zbd-pdf-viewer",
    templateUrl: "pdf-viewer.component.html",
    styles: [
        `
            .waiting,
            .error {
                padding: 1em;
                text-align: center;
                font-size: 160%;
            }

            .waiting .message {
                vertical-align: middle;
                padding-left: 0.5em;
            }

            .error-info {
                padding: 0ex 2em;
            }

            .error-details {
                padding: 2ex 2em;
            }

            .error-details-header {
                font-weight: bold;
            }

            .waiting > .spinning {
                vertical-align: middle;
                font-size: 32px;
            }

            .toolbar-link {
                color: inherit;
            }

            .pdf-renderer-container {
                width: 742px; /* TODO: Where is this defined? */
                margin-left: auto;
                margin-right: auto;
            }

            .zas-pdf-viewer-toolbar:hover {
                text-decoration: none;
            }

            .zas-pdf-viewer-toolbar-item-action-available,
            .zas-pdf-viewer-toolbar-item-action-not-available {
                font-size: 18px;
            }
        `,
    ],
})
export class PdfViewerComponent
    extends UtilsComponent
    implements AfterViewInit
{
    @ViewChild(PdfRendererComponent) renderer: PdfRendererComponent;
    @ViewChild(SignModal) signModal: SignModal;
    @ViewChild(SignAllModal) signAllModal: SignAllModal;
    @ViewChild(EmailFormModal) emailFormModal: EmailFormModal;
    @ViewChild(QRCodeModal) qrCodeModal: QRCodeModal;
    @ViewChild(FinalizingModal) finalizingModal: FinalizingModal;

    state$: Observable<LocalState>;
    validity$: Observable<ValidityState>;

    signpadAvailable = false;
    signpadQueryFinished = false;
    hideEmailFunction = false;

    constructor(
        private esignService: EsignService,
        private signpad: SignpadService,
        private viewContainer: ViewContainerRef,
        private keycloak: KeycloakService,
        public consultationService: ConsultationService,
        public persistence: PersistenceService,
        public store: Store<RootState>,
    ) {
        super();

        this.state$ = LocalState.create(store, esignService.esignView$);
        this.validity$ = store.select<ValidityState>((s) => s.validity);
    }

    ngAfterViewInit(): void {
        this.register(
            this.store
                .select((s) => s.document.dirty)
                .pipe(
                    distinctUntilChanged(),
                    withLatestFrom(
                        this.store
                            .select((s) =>
                                s.view.selectedMainSection
                                    ? s.view.selectedMainSection.key
                                    : null,
                            )
                            .pipe(distinctUntilChanged()),
                    ),
                )
                .subscribe(([isDirty, visibleSectionKey]) => {
                    if (isDirty && visibleSectionKey === "document") {
                        this.closeModals();
                        this.store.dispatch(new DocActions.Build());
                    }
                }),
        );
        this.checkHideEmailFunctionValue();
    }

    private checkHideEmailFunctionValue(): void {
        this.store
            .select(
                (s) => s.model.ConsultationProcess.Protocol.Offers[0].Divisions,
            )
            .pipe(
                map((d) =>
                    d.reduce((prev: boolean, div: Division) => {
                        return (
                            prev ||
                            this.isAccident(div) ||
                            this.isLife(div) ||
                            this.isOtherProducts(div)
                        );
                    }, false),
                ),
            )
            .subscribe((hide) => (this.hideEmailFunction = hide))
            .unsubscribe();
    }

    private isAccident(div: Division): boolean {
        return div.GroupKey === "unfall";
    }

    private isLife(div: Division): boolean {
        return div.CategoryKey === "leben";
    }

    private isOtherProducts(div: Division): boolean {
        const otherProducts = [
            "fkBavDirektversicherung",
            "fkBavPensionszusage",
            "drittIdealSterbegeld",
            "drittIdealPflegeRente",
            "drittDKVPflegeZusatz",
            "drittDKVKrankenzusatz",
            "drittEagleKrankheitsschutzbrief",
            "drittEagleRisikolebenTop",
            "drittEagleRisikolebenBasic",
        ];
        return otherProducts.includes(div.ProductKey);
    }

    public closeModals(): void {
        this.signModal.close();
        this.signAllModal.close();
        this.qrCodeModal.close();
        this.emailFormModal.close();
        this.finalizingModal.close();
    }

    public buildDocument() {
        this.store.dispatch(new DocActions.Build(false));
    }

    public createPdf(): void {
        this.store.dispatch(new DocActions.Build());
    }

    public openInNewTab(blob: Blob): void {
        if (!blob) {
            return;
        }
        Helpers.openBlobInNewTab(blob);
    }

    onPageNumberChange(state: LocalState, page: number) {
        page = Math.max(1, page);
        page = Math.min(page, state.document.pageCount);
        this.setPage(page);
        // TODO: Textbox value is not clipped
    }

    private setPage(page: number): void {
        this.store.dispatch(
            DocActions.Update.create().set("currentPage", page),
        );
    }

    goToFirstPage(state: LocalState) {
        if (!state.esignView.onFirstPage) {
            this.setPage(1);
        }
    }

    goToPrevPage(state: LocalState) {
        if (!state.esignView.onFirstPage) {
            this.setPage(state.document.currentPage - 1);
        }
    }

    goToNextPage(state: LocalState) {
        if (!state.esignView.onLastPage) {
            this.setPage(state.document.currentPage + 1);
        }
    }

    goToLastPage(state: LocalState) {
        if (!state.esignView.onLastPage) {
            this.setPage(state.document.pageCount);
        }
    }

    showEmailSendForm(cp: ConsultationProcess): void {
        this.emailFormModal.open(cp);
    }

    showQRCode() {
        this.qrCodeModal.open();
    }

    showSignAll(docState: DocumentState) {
        this.signAllModal.open();
    }

    onSignFieldClicked(field: Signature): void {
        this.signpad.service.capture(
            "test",
            field.metadata.Label,
            field.width,
            field.height,
            (img) => {
                this.store.dispatch(
                    new DocActions.SetSignature(field.index, img.src),
                );
            },
            (error) => {
                this.signModal.open(field);
            },
            (error) => {
                this.signModal.open(field);
            },
        );
    }

    onQRCodeClosed(): void {
        this.persistence.reload();
    }

    public finalizeProtocol(cp: ConsultationProcess) {
        this.closeModals();
        this.finalizingModal.open();
        this.store.dispatch(new DocActions.Finalize());
    }

    get mayUseQRCode(): boolean {
        const userInfo = new UserInfo(this.keycloak.getExistingParsedToken());
        return !userInfo.isDb();
    }
}

export class LocalState {
    cp: ConsultationProcess;
    document: DocumentState;
    esignView: EsignViewInfo;

    static create(
        store: Store<RootState>,
        esignView$: Observable<EsignViewInfo>,
    ): Observable<LocalState> {
        return stateBuilder(store, new LocalState())
            .apply("cp", (s) => s.model.ConsultationProcess)
            .apply("document", (s) => s.document)
            .observe("esignView", esignView$).state$;
    }
}
